/**
 * SubscriptionSettingsCtrl controller
 */

import moment from 'moment'
import { CURRENCY_CONVERTER_TYPES, PAYMENT_SYSTEM_TYPES } from 'app/core/constants'
import { find } from 'app/helpers/object'
import { remove } from 'app/helpers/entity'

// controller mixins
SubscriptionSettingsCtrl.mixes = [
  'onOffCtrlMixin'
]

/**
 * SubscriptionSettingsCtrl controller
 *
 * @class SubscriptionSettingsCtrl
 * @memberof mwApp/packages
 * @see mwApp/packages
 * @mixes mwApp/common.mixin:onOffCtrlMixin
 *
 * @ngdoc controller
 * @name mwApp/packages.controller:SubscriptionSettingsCtrl
 */
/* @ngInject */
export default function SubscriptionSettingsCtrl (
  $location,
  $scope,
  $uibModal,
  NetworkCurrency,
  NetworkCurrencyConverter,
  NetworkPaymentSystem,
  NetworkSetting,
  Subscription,
  alertSvc,
  authSvc,
  ecwid,
  inapp,
  pckg
) {
  var vm = this
  vm.mixes = [['tabsCtrlMixin', 'subscriptions', 'accessories']]

  vm.APP_DEVICE_LINK_CODE_ENABLED = !!window.ENV.APP_DEVICE_LINK_CODE_ENABLED
  vm.FEATURE_EXCLUSIVE_PAYMENT_METHODS_ENABLED = authSvc.hasEnabledFlag('FEATURE_EXCLUSIVE_PAYMENT_METHODS_ENABLED')

  vm.platform_labels = {
    STB: 'STB',
    WEB: 'WEB',
    PC: 'LG',
    iOS: 'iOS',
    TVOS: 'tvOS',
    Android: 'Android',
    SamsungTV: 'SamsungTv',
    Others: 'Others'
  }

  /**
   * Platforms array
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @type {Object[]}
   */
  vm.platforms = pckg._enhancePlatforms().map(platform => platform.name)

  /**
   * Has access channel constant
   *
   * @memberOf mwApp/packages.controller:SubscriptionSettingsCtrl
   * @function
   */
  vm.hasAccess = function (access) {
    return authSvc.hasAccess(access)
  }

  /**
   * Loader indicator
   *
   * @type {boolean}
   */
  $scope.loading = true

  /**
   * Promise stack
   *
   * @type {Array}
   */
  vm.loaderStack = []

  /**
   * Package/Network
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @type {models.Network}
   */
  vm.pckg = pckg

  /**
   * Default Package for in-app registration
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @type {number}
   */
  vm.inappNetworkId = !vm.pckg.multiorgEnabled && inapp?.inAppRegistrationSettings?.enabled && inapp.inAppRegistrationSettings.network?.id

  /**
   * Default Package for Guest Mode
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @type {number}
   */
  vm.guestNetworkId = !vm.pckg.multiorgEnabled && inapp?.guestModeSettings?.enabled && inapp.guestModeSettings.network?.id

  /*********************************************************************************************************************************/
  /* === Payment Sets                                                                                                          === */
  /*********************************************************************************************************************************/

  /**
   * Subscription types
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @type {Object}
   */
  vm.subscription_types = { Subscription: 'Subscription', Accessory: 'Accessory' }

  if (authSvc.hasEnabledFlag('ECOMMERCE_PROVIDER_ENABLED')) {
    vm.pckg.$ecwidEnabled = ecwid ? !!ecwid.enabled : false
  }

  if (!vm.pckg.$ecwidEnabled) {
    delete vm.subscription_types['Subscription ECWID']
  }

  /**
   * Info message for deleting subscription
   *
   * @memberof mwApp/settings.controller:SubscriptionSettingsCtrl
   *
   * @param  subscription
   * @return {string}
   */
  vm.getOnRemoveSubMessage = function (subscription) {
    const message = 'Are you sure you want to delete Subscription permanently?'
    const usedBy = ['ACTIVE_SUBSCRIBER', 'VOUCHER_BATCH', 'IN_APP_REGISTRATION']
    const confirmationCombination = usedBy.reduce((acc, item) => acc + (subscription.usedBy?.includes(item) ? '+' : '-'), '')

    switch (confirmationCombination) {
      case '+--':
        return `Currently Subscription has active Subscribers. If deleted, access to Client App will be disabled for Subscribers activated with this Subscription.<br><br>${message}`
      case '++-':
        return `Currently Subscription has active Subscribers and selected for Voucher Batch(es). If deleted, access to Client App will be disabled for Subscribers activated with Subscription.<br><br>${message}`
      case '+++':
        return `Currently Subscription has active Subscribers, selected for Voucher Batch(es) and set as default for In-App Registration/Guest Mode. If deleted, access to Client App will be disabled for Subscribers activated with Subscription and In-App Registration/Guest Mode will be disabled.<br><br>${message}`
      case '-++':
        return `Currently Subscription is selected for Voucher Batch(es) and set as default for In-App Registration/Guest Mode. If deleted, assigned content will not be available for Subscribers activated with Subscription and In-App Registration/Guest Mode will be disabled.<br><br>${message}`
      case '--+':
        return `Currently Subscription is set as default for In-App Registration/Guest Mode. If deleted, In-App Registration/Guest Mode will be disabled.<br><br>${message}`
      case '+-+':
        return `Currently Subscription has active Subscribers and set as default for In-App Registration/Guest Mode. If deleted, access to Client App will be disabled for Subscribers activated with Subscription and In-App Registartion/Guest Mode will be disabled.<br><br>${message}`
      case '-+-':
        return `Currently Subscription is selected for Voucher Batch(es). If deleted, access to Client App will be disabled for Subscribers activated with Subscription.<br><br>${message}`
      default:
        return message
    }
  }

  /**
   * Subscriptions list
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @name vm.subscriptions
   * @type {models.Subscription[]}
   */

  vm.subscriptions = []
  Subscription.loadAll({ networkId: pckg.id })
    .then(data => vm.handleSubscriptions(data.filter(item => item.subscriptionType === 'Subscription')))
    .to(vm.subscriptions = [])

  vm.handleSubscriptions = function (subscriptions) {
    subscriptions.forEach(sub => {
      sub.$onRemoveMessage = vm.getOnRemoveSubMessage(sub)
      if (sub.trial) {
        sub.$trialLabel = 'Allowed'
      } else if (sub.subscriptionType === 'Subscription') {
        sub.$trialLabel = 'No'
      } else {
        sub.$trialLabel = '--'
      }
      if (sub.subscriptionType !== 'Subscription') {
        sub.platforms = []
      } else if (sub.useNetworkPlatforms) {
        sub.platforms = pckg.platforms
      }

      if (sub.exclusivePaymentSystemSetting?.assignedPaymentSystems.length) {
        sub.$paymentSystemTypes = sub.exclusivePaymentSystemSetting.assignedPaymentSystems.map(ps => ps.paymentSystemType)
      } else {
        sub.$paymentSystemTypes = ['No_Limitations']
      }
    })
    return subscriptions
  }

  /**
   * Delete subscription
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @param  {models.Subscription} subscription
   */
  vm.deleteSubscription = function (subscription) {
    Subscription.loadDiscountCampaigns(subscription).then((res) => {
      if (res.length) {
        const msg = {
          text: 'Are you sure you want to delete Subscription permanently?',
          header: 'Subscription is a targeted product for the actual discount campaign(s): ' + res.map((dc) => dc.name).join(', ')
        }
        alertSvc.confirm(msg)
          .then(
            () => {
              subscription._delete()
                .then(remove(vm.subscriptions)(subscription))
            }
          )
      } else {
        subscription._delete()
          .then(remove(vm.subscriptions)(subscription))
      }
    })
  }

  /*********************************************************************************************************************************/
  /* === Payment Services                                                                                                      === */
  /*********************************************************************************************************************************/

  /**
   * Payment system types
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @type {Object}
   */
  vm.payment_system_types = { ...PAYMENT_SYSTEM_TYPES, No_Limitations: 'No limitations' }

  vm.payment_system_types_array = pckg.allowedExclusivePaymentSystems.map(ps => ps.paymentSystemType).concat('No_Limitations')

  /**
   * NetworkPaymentSystem services list
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @name vm.payments
   * @type {models.NetworkPaymentSystem[]}
   */

  // current default payment
  var defaultPaymentSystem = null

  // load payment systems
  vm.loaderStack.push(NetworkPaymentSystem.loadAll({ network: pckg })
    .to(vm.payments = [])
    .then(
      () => {
        vm.payments = vm.payments.filter(p => !['TELR', 'NEWEBPAY'].includes(p.paymentSystemType))
        vm.payments.forEach((p) => {
          p.$network = pckg // set fake field `$network` to use it in links
          p._updateUrl() // and update url for every payment system
        })
        defaultPaymentSystem = find(p => p.defaultPaymentSystem)(vm.payments) || vm.payments[0]
      }
    ))

  /**
   * Check loader
   */
  Promise.all(vm.loaderStack)
    .then(() => { $scope.loading = false })

  /**
   * Set payment as default
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @param  {models.NetworkPaymentSystem} payment
   * @param setDefault
   */
  vm.setDefault = function (payment, setDefault) {
    payment._default(setDefault)
      .then(
        () => {
          if (defaultPaymentSystem && payment !== defaultPaymentSystem) {
            defaultPaymentSystem.defaultPaymentSystem = false
          }
          defaultPaymentSystem = payment
        }
      )
  }

  /**
   * Set payment as default
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   * @param  {models.NetworkPaymentSystem} payment
   */
  vm.systemDefaultPayment = function (payment) {
    payment._systemDefault()
  }

  /*********************************************************************************************************************************/
  /* === Currency Converter                                                                                                    === */
  /*********************************************************************************************************************************/

  /**
   * Currency converters types
   *
   * @memberof mwApp/settings.controller:SubscriptionSettingsCtrl
   * @type {Object}
   */
  vm.currency_converter_types = CURRENCY_CONVERTER_TYPES

  /**
   * Get NetworkCurrencyConverter list
   *
   * @memberof mwApp/settings.controller:SubscriptionSettingsCtrl
   * @name vm.converters
   * @type {models.PaymentSystem[]}
   */

  // default currency converter
  let defaultConverter = null

  // load currency converters
  NetworkCurrencyConverter.loadAll({ $network: pckg }) // use fake field `$network`
    .to(vm.converters = [])
    .then(
      () => {
        vm.converters.forEach((c) => {
          c.$network = pckg // set fake field `$network` to use it in links
          c._updateUrl() // and update url for every payment system
        })
        defaultConverter = find(c => c.defaultConverter)(vm.converters) || vm.converters[0]
      }
    )

  /**
   * Format currency converter rate
   *
   * @memberof mwApp/settings.controller:SubscriptionSettingsCtrl
   * @function
   * @param  {Number} seconds refresh rate
   */
  vm.formatRefreshRate = (seconds) => {
    if (seconds) {
      const duration = moment.duration(seconds * 60, 'seconds') // multiply by 60, because in the `refreshRateSec` field already minutes
      const [h, m, s] = [duration.hours(), duration.minutes(), duration.seconds()]
      return (h ? `${h} hours ` : '') + (m ? `${m} mins ` : '') + (s ? `${s} seconds` : '')
    }
    return 'Live'
  }

  vm.onOffCurrency = function (currency) {
    const msg = {
      text: `Payment Sets with selected currency ${currency.name} will not be available for making Payments</br></br>` +
        `Making payments with Payment Services with allowed currency ${currency.name} might not be available if any other currency is not set</br></br></br>` +
        `Are you sure you want to disable currency ${currency.name} for Organization?`,
      header: 'Disable Currency'
    }
    if (!currency.enabled) {
      alertSvc.confirm(msg).then(() => {
        vm.onOff(currency)
      })
        .catch(() => {
          currency.enabled = true
        })
    } else {
      vm.onOff(currency)
    }
  }

  vm.onOffSubscription = function (subscription) {
    if (subscription.$currentEnabled) {
      Subscription.loadDiscountCampaigns(subscription).then((res) => {
        if (res.length) {
          const msg = {
            text: 'Are you sure you want to disable Subscription?',
            header: 'Subscription is a targeted product for the actual discount campaign(s): ' + res.map((dc) => dc.name).join(', ')
          }
          alertSvc.confirm(msg)
            .then(
              () => {
                vm.onOff(subscription).then(() => {
                  subscription.$currentEnabled = subscription.enabled
                })
              }
            )
            .catch(() => {
              subscription.enabled = true
            })
        } else {
          vm.onOff(subscription).then(() => {
            subscription.$currentEnabled = subscription.enabled
          })
        }
      })
    } else {
      vm.onOff(subscription).then(() => {
        subscription.$currentEnabled = subscription.enabled
      })
    }
  }

  vm.disableSubscription = function (subscription) {
    subscription.enabled = false
  }

  vm.showDueDateError = function (subscription) {
    if (subscription.dueDate) {
      const dueDate = new Date(subscription.$dueDate?.setHours(23, 59, 59, 999))
      return new Date().setHours(23, 59, 59, 999) > dueDate
    }
    return false
  }

  /**
   * Set converter as default
   *
   * @memberof mwApp/settings.controller:SubscriptionSettingsCtrl
   * @param  {models.NetworkCurrencyConverter} converter
   * @param setDefault
   */
  vm.setDefaultConverter = function (converter, setDefault) {
    converter._default(setDefault)
      .then(
        () => {
          if (defaultConverter && converter !== defaultConverter) {
            defaultConverter.defaultConverter = false
          }
          defaultConverter = converter
        }
      )
  }

  /**
   * Set system default
   *
   * @memberof mwApp/settings.controller:SubscriptionSettingsCtrl
   * @param  {models.NetworkCurrencyConverter} converter
   */
  vm.systemDefaultConverter = function (converter) {
    converter._systemDefault()
  }

  /**
   * redirection to 'generalsettings'
   *
   * @memberof mwApp/packages.controller:SubscriptionSettingsCtrl
   */
  vm.redirectToSettings = function () {
    vm.pckg.multiorgEnabled ? $location.url(`organizations/${vm.pckg.id}#activation_settings`) : $location.url('generalsettings')
  }

  vm.showLength = function (subscription) {
    switch (subscription.$periodType) {
      case 'DUE_DATE':
        return `Until: ${moment(subscription.dueDate).format('DD.MM.YYYY')}`
      case 'DEFAULT':
        return subscription.length + ' ' + subscription.lengthType
      default:
        return 'Unlimited'
    }
  }
}
